import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Renders text on a single line and shrinks the font-size until it fits the
 * available width instead of wrapping. Used for deployed-app tile titles where
 * a long name must stay on one line but must not overflow its box.
 *
 * @param {string} text          Text to display.
 * @param {number} maxFontSize   Starting (largest) font size in px.
 * @param {number} [minFontSize] Smallest font size in px before we stop shrinking.
 * @param {object} [style]       Extra styles merged onto the span (color, weight…).
 * @param {string} [title]       Native tooltip; defaults to the full text.
 */
export default function FitText({ text, maxFontSize, minFontSize = 10, style, title, ...rest }) {
    const ref = useRef(null);
    const [fontSize, setFontSize] = useState(maxFontSize);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        const fit = () => {
            let size = maxFontSize;
            el.style.fontSize = `${size}px`;
            // Shrink one px at a time while the single-line content overflows its
            // box. The guard bounds the loop so a pathological layout can't hang.
            let guard = 0;
            while (el.scrollWidth > el.clientWidth && size > minFontSize && guard < 200) {
                size -= 1;
                el.style.fontSize = `${size}px`;
                guard += 1;
            }
            setFontSize(size);
        };

        // Defer re-fits triggered by the ResizeObserver into the next animation
        // frame. Mutating font-size synchronously inside the observer callback
        // re-lays-out the observed parent in the same frame, which the browser
        // reports as "ResizeObserver loop completed with undelivered
        // notifications" (surfaced as an uncaught error by the CRA dev overlay).
        let raf = 0;
        const scheduleFit = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => { raf = 0; fit(); });
        };

        fit();

        let ro;
        if (typeof ResizeObserver !== 'undefined' && el.parentElement) {
            ro = new ResizeObserver(scheduleFit);
            ro.observe(el.parentElement);
        }
        return () => {
            if (raf) cancelAnimationFrame(raf);
            if (ro) ro.disconnect();
        };
    }, [text, maxFontSize, minFontSize]);

    return (
        <span
            ref={ref}
            title={title != null ? title : text}
            style={{
                display: 'block',
                width: '100%',
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...style,
                fontSize: `${fontSize}px`,
            }}
            {...rest}
        >
            {text}
        </span>
    );
}
